import axios from 'axios';
import {
  clearLoginState,
  getAccessToken,
  getRefreshToken,
  setAccessToken,
} from './auth';

const API_BASE_URL = process.env.VUE_APP_API_BASE_URL || '/api';

axios.defaults.withCredentials = true;

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  },
  timeout: 60000
});

let routerInstance = null;

export const bindAuthRouter = (router) => {
  routerInstance = router;
};

const redirectToLogin = () => {
  clearLoginState();
  if (routerInstance) {
    if (routerInstance.currentRoute.value.path !== '/login') {
      routerInstance.push('/login');
    }
    return;
  }
  window.location.assign('/login');
};

axiosInstance.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    const requestUrl = originalRequest?.url || '';
    const skipRefresh = requestUrl.includes('/admin/login') || requestUrl.includes('/admin/refresh');

    if (error.response && error.response.status === 401 && originalRequest && !originalRequest._retry && !skipRefresh) {
      originalRequest._retry = true;

      try {
        const refreshToken = getRefreshToken();
        if (!refreshToken) {
          redirectToLogin();
          return Promise.reject(error);
        }

        const response = await axiosInstance.post('/admin/refresh', {
          refresh_token: refreshToken
        });
        const newAccessToken = response.data.access_token;
        setAccessToken(newAccessToken);
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        redirectToLogin();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

const authApi = {
  getUpdateInfo: () => axiosInstance.get('/get_update_info'),

  register: (userData) => axiosInstance.post('/register', userData),

  getAllUsers: (params) => axiosInstance.get('/get_all_users', { params }),

  confirmRequest: (userId, licenseDuration, authVersion) =>
    axiosInstance.post('/confirm_request', {
      user_id: userId,
      license_duration: licenseDuration,
      auth_version: authVersion
    }),

  getCaptcha: (sessionId) => {
    const url = `/captcha?session_id=${sessionId}`;
    return axiosInstance.get(url, { responseType: 'blob' })
      .then((response) => URL.createObjectURL(new Blob([response.data])));
  },

  adminLogin: (username, password) =>
    axiosInstance.post('/admin/login', { username, password }),

  refreshToken: (refreshToken) =>
    axiosInstance.post('/admin/refresh', { refresh_token: refreshToken }),

  changePassword: (currentPassword, newPassword) =>
    axiosInstance.post('/admin/change-password', {
      current_password: currentPassword,
      new_password: newPassword
    }),

  addNewAuth: (authData) => axiosInstance.post('/add_new_auth', {
    ...authData,
    licenseDuration: parseInt(authData.licenseDuration, 10)
  }),

  getAllLoginLogs: (params) =>
    axiosInstance.get('/get_all_login_logs', { params }),

  getAllBugReports: (params) =>
    axiosInstance.get('/get_all_bug_reports', { params }),

  submitInterest: (userData) =>
    axiosInstance.post('/submit_interest', userData),

  getAllInterestedUsers: (params) =>
    axiosInstance.get('/get_all_interested_users', { params }),

  markInterestedUserAsProcessed: (userId) =>
    axiosInstance.post('/mark_interested_user_as_processed', { user_id: userId }),
};

export default authApi;
